import { qpxToast as toast } from '@quipex/core/utils';
import {
    filter,
    groupBy,
    map,
    mergeMap,
    of,
    switchMap,
    tap,
    timer,
    type Observable,
} from 'rxjs';
import { isDocumentImportSizeFailure } from './document-import-size-failure';
import type { JobProgressMessage } from './models';
import {
    toRealtimeJobLabel,
    toRealtimeStatusLabel,
    toRealtimeToastDescription,
    toRealtimeToastDuration,
} from './realtime-job-notification-copy';

const NON_TERMINAL_TOAST_DEBOUNCE_MS = 1_000;

export function createRealtimeJobNotificationToasts(
    jobProgress$: Observable<JobProgressMessage>
): Observable<void> {
    return jobProgress$.pipe(
        filter(
            (message) =>
                message.jobType !== 'BuildingDocumentExport' &&
                message.jobType !== 'SelectedBuildingDocumentExport' &&
                message.jobType !== 'DocumentPackExport'
        ),
        groupBy((message) => message.jobId),
        mergeMap((messagesForJob$) =>
            messagesForJob$.pipe(
                switchMap((message) =>
                    isTerminalRealtimeStatus(message.status)
                        ? of(message)
                        : timer(NON_TERMINAL_TOAST_DEBOUNCE_MS).pipe(
                              map(() => message)
                          )
                )
            )
        ),
        tap((message) => showRealtimeJobToast(message)),
        map(() => undefined)
    );
}

function showRealtimeJobToast(message: JobProgressMessage): void {
    const toastTitle = `${toRealtimeJobLabel(message.jobType)} ${toRealtimeStatusLabel(
        message.status
    )}.`;
    const toastOptions = {
        id: `realtime-job:${message.jobId}`,
        duration: toRealtimeToastDuration(message.status),
        description: toRealtimeToastDescription(message),
    };

    switch (message.status) {
        case 'Completed':
            if (
                message.jobType === 'DocumentImport' &&
                isDocumentImportSizeFailure(message.failureCode)
            ) {
                toast.warning(toastTitle, toastOptions);
                return;
            }

            toast.success(toastTitle, toastOptions);
            return;

        case 'Failed':
            toast.error(toastTitle, toastOptions);
            return;

        case 'Skipped':
        case 'Expired':
            toast.warning(toastTitle, toastOptions);
            return;

        default:
            toast.info(toastTitle, toastOptions);
    }
}

function isTerminalRealtimeStatus(
    status: JobProgressMessage['status']
): boolean {
    return (
        status === 'Completed' ||
        status === 'Failed' ||
        status === 'Skipped' ||
        status === 'Expired'
    );
}
